// My Practice: everything added, with a seven-day strip per practice.
// Filters are two-level, following the ontology: domains lead, and a domain
// reveals its topics only when it holds more than one in your plan. The
// deferred and passed lists are filters in the same row (29 Jul 2026,
// replacing the rail cards), each appearing only while it holds something.
// Your own practices live here too (29 Jul 2026): created from the action
// beside the filter bar, marked Yours, private to the account.
import { useRef, useState } from "react";
import type { Descriptor } from "../shell/descriptor.js";
import { DOMAIN_FILTERS } from "../copy.js";
import { Avatar, avatarTitle } from "../shell/Avatar.js";
import { doseNote, MetaLine, useExpert, WeekStrip, YOURS_AVATAR, YoursChip } from "../shell/fragments.js";
import { artFor } from "../shell/placeholderArt.js";
import { useStore, type AppStore } from "../state/store.js";
import type { PlanItemView } from "../state/types.js";
import { groupPassed, LaterRow, PassedRow, PausedRow } from "./discover.js";
import { artCss } from "../assets.js";

function prettyDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

interface DragBinding {
  index: number;
  dragging: boolean;
  onGrab: () => void;
  onOver: () => void;
  onDrop: () => void;
  onNudge: (delta: number) => void;
  total: number;
}

function PlanCard({ item, drag }: { item: PlanItemView; drag: DragBinding }) {
  const s = useStore();
  const card = s.appState?.cards[item.cardId];
  const e = useExpert(card?.expertId ?? "");
  if (!card || !s.appState) return null;
  const yours = card.origin === "user";
  const topic = s.appState.topics.find((t) => t.id === card.topicId);
  const done = item.week.includes(s.appState.today);
  const open = () => s.openSheet({ kind: "practice", cardId: card.id });
  return (
    // The whole card opens the practice modal; every control inside stops
    // propagation so a tick or a menu is never also a card tap. The title
    // button stays as the keyboard way in.
    //
    // It is also the drag handle (31 Jul 2026, Rob): a plain click still
    // opens the modal, because a browser fires no click at the end of a
    // drag. The grip in the corner is the advertisement, and the keyboard
    // path, since HTML5 dragging has none of its own.
    <div
      className="sug imgbg"
      data-done={done}
      data-dragging={drag.dragging || undefined}
      style={{ cursor: "pointer", ["--photo" as string]: artCss(artFor(card)) }}
      onClick={open}
      draggable
      onDragStart={(ev) => {
        // Firefox starts no drag without payload; the id travels in state.
        ev.dataTransfer.effectAllowed = "move";
        ev.dataTransfer.setData("text/plain", item.id);
        drag.onGrab();
      }}
      onDragOver={(ev) => {
        ev.preventDefault();
        ev.dataTransfer.dropEffect = "move";
        drag.onOver();
      }}
      onDrop={(ev) => {
        ev.preventDefault();
        drag.onDrop();
      }}
      onDragEnd={() => drag.onDrop()}
    >
      <div className="top">
        <button
          className="grip"
          aria-label={`Reorder: ${card.do}, ${drag.index + 1} of ${drag.total}`}
          title="Drag to reorder, or use the arrow keys"
          onClick={(ev) => ev.stopPropagation()}
          onKeyDown={(ev) => {
            if (ev.key !== "ArrowUp" && ev.key !== "ArrowDown") return;
            ev.preventDefault();
            drag.onNudge(ev.key === "ArrowUp" ? -1 : 1);
          }}
        >
          ⠿
        </button>
        <Avatar e={yours ? YOURS_AVATAR : e} size="s" title={yours ? undefined : avatarTitle(e)} />
        {/* Whose practice it is leads the line, beside their face, and the
            date it joined follows (31 Jul 2026, Rob). One line: a long
            institution name ellipses rather than pushing the tick down. */}
        <span className="planwho">
          <b>{yours ? "Yours" : e.name}</b> · Added {prettyDate(item.addedAt)}
        </span>
        <button
          className="tick"
          style={{ width: 32, height: 32, borderRadius: 11, fontSize: 14, marginLeft: "auto" }}
          aria-pressed={done}
          aria-label="Done today"
          onClick={(ev) => {
            ev.stopPropagation();
            void s.toggleComplete(item.id, s.appState?.today ?? "", !done);
          }}
        >
          ✓
        </button>
      </div>
      <button
        className="do"
        style={{ display: "block", textAlign: "left" }}
        onClick={(ev) => {
          ev.stopPropagation();
          open();
        }}
      >
        {card.do}
      </button>
      <WeekStrip planItemId={item.id} week={item.week} today={s.appState.today} />
      <div className="btnrow" style={{ justifyContent: "space-between" }}>
        <MetaLine
          parts={[
            yours && <YoursChip />,
            topic?.short,
            doseNote(card, item.chosenVersion),
            <span key="w" className="weekn">{item.week.length}/7</span>,
          ]}
        />
        {/* Expert practices: the same two answers as anywhere, in plan
            context (both stop the practice first; Later returns on its
            date). Your own practice takes a break instead: Later pauses in
            place, and Edit opens the same sheet that created it. */}
        <span className="btnrow">
          <button
            className="btn quiet"
            onClick={(ev) => {
              ev.stopPropagation();
              const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
              if (yours) {
                s.openPopover({ kind: "pause", planItemId: item.id, x: r.right, y: r.bottom });
              } else {
                s.openPopover({ kind: "snooze", cardId: card.id, x: r.right, y: r.bottom, planItemId: item.id });
              }
            }}
          >
            Later ▾
          </button>
          {yours ? (
            <button
              className="btn quiet"
              onClick={(ev) => {
                ev.stopPropagation();
                s.openSheet({ kind: "create-practice", cardId: card.id });
              }}
            >
              Edit
            </button>
          ) : (
            <button
              className="btn quiet"
              onClick={(ev) => {
                ev.stopPropagation();
                const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                s.openPopover({ kind: "decline", cardId: card.id, x: r.right, y: r.bottom, planItemId: item.id });
              }}
            >
              Stop ▾
            </button>
          )}
        </span>
      </div>
    </div>
  );
}

// The plan in the user's own order (31 Jul 2026, Rob). The order lives on
// the server and is what the day's check-list reads, so dragging here is
// how someone says what their morning looks like. Newest is always first
// until they move it; the server does that on the way in.
//
// Reordering inside a filter reorders only those cards, in the slots those
// same cards already held: the practices the filter is hiding do not move
// because of a decision the user could not see them take part in.
function PlanGrid({ items }: { items: PlanItemView[] }) {
  const reorderPlan = useStore((s) => s.reorderPlan);
  // The order and what is held live in refs as well as state, and the refs
  // are what the handlers read. A pointer crossing two cards can fire two
  // dragovers before React has re-rendered once, and a handler reading last
  // render's state would answer with a card nobody is holding any more.
  // State is here to draw with; the ref is the truth.
  const [, redraw] = useState(0);
  const orderRef = useRef<string[] | null>(null);
  const heldRef = useRef<string | null>(null);

  const ids = items.map((p) => p.id);
  // A drag owns the order until it lands. Anything that changed the plan
  // underneath (a practice added, one stopped) drops back to the store's.
  const resolve = (): string[] => {
    const o = orderRef.current;
    return o && o.length === ids.length && o.every((id) => ids.includes(id)) ? o : ids;
  };
  const live = resolve();
  const shown = live
    .map((id) => items.find((p) => p.id === id))
    .filter((p): p is PlanItemView => p !== undefined);

  const moveTo = (id: string, to: number): string[] | null => {
    const from = resolve();
    const at = from.indexOf(id);
    if (at < 0 || to < 0 || to >= from.length || at === to) return null;
    const next = [...from];
    next.splice(to, 0, ...next.splice(at, 1));
    orderRef.current = next;
    redraw((n) => n + 1);
    return next;
  };

  return (
    <div className="grid2 plangrid">
      {shown.map((item, i) => (
        <PlanCard
          key={item.id}
          item={item}
          drag={{
            index: i,
            total: shown.length,
            dragging: heldRef.current === item.id,
            onGrab: () => {
              heldRef.current = item.id;
              redraw((n) => n + 1);
            },
            // The list shuffles under the cursor rather than at the drop, so
            // the gap the card is going into is visible before letting go.
            onOver: () => {
              const held = heldRef.current;
              if (held && held !== item.id) moveTo(held, i);
            },
            // Drop and dragend both land here: a drag cancelled outside the
            // grid still commits what the user watched happen. Dragend also
            // follows every drop, and the second call finds nothing held.
            onDrop: () => {
              if (!heldRef.current) return;
              const next = orderRef.current;
              heldRef.current = null;
              orderRef.current = null;
              if (next) void reorderPlan(next);
              redraw((n) => n + 1);
            },
            onNudge: (delta) => {
              const next = moveTo(item.id, i + delta);
              if (!next) return;
              orderRef.current = null;
              void reorderPlan(next);
            },
          }}
        />
      ))}
    </div>
  );
}

export function practices(state: AppStore): Descriptor {
  const s = state.appState;
  if (!s) {
    return {
      nav: "practices",
      title: "My Practice",
      main: <section className="panel glass"><p className="empty">Loading…</p></section>,
    };
  }

  const all = s.plan.filter((p) => p.status === "active");
  const domainOf = (item: PlanItemView) => {
    const t = s.topics.find((x) => x.id === s.cards[item.cardId]?.topicId);
    return t?.domain ?? null;
  };
  const usedDomains = [...new Set(all.map(domainOf).filter((d): d is string => Boolean(d)))];

  const later = s.discover.later;
  // Your own practices taking a break wait under Deferred alongside the
  // snoozed ones; the return path differs, the promise is the same.
  const paused = s.plan.filter((p) => p.status === "paused" && s.cards[p.cardId]?.origin === "user");
  const deferredN = later.length + paused.length;
  const passedGroups = groupPassed(s.discover.passed);
  // A state filter whose last row was undone falls back to All rather than
  // stranding the user on an empty list under a chip that no longer exists.
  const stateFilter =
    state.planDomain === "later" && deferredN
      ? "later"
      : state.planDomain === "passed" && passedGroups.length
        ? "passed"
        : null;
  const domainSel =
    stateFilter || state.planDomain === "later" || state.planDomain === "passed" ? "all" : state.planDomain;

  let list = all;
  if (state.planTopic) list = all.filter((p) => s.cards[p.cardId]?.topicId === state.planTopic);
  else if (domainSel !== "all") list = all.filter((p) => domainOf(p) === domainSel);
  const doneN = list.filter((p) => p.week.includes(s.today)).length;

  // The filters are the seg bar now (29 Jul 2026, one filter bar on every
  // surface); the count renders as the tab's own count element. The state
  // filters and the domains share it: they were one chip row before.
  const tabItems: Array<[string, string]> = [["all", "Active"]];
  const tabCounts: Record<string, number> = { all: all.length };
  if (deferredN > 0) {
    tabItems.push(["later", "Deferred"]);
    tabCounts.later = deferredN;
  }
  if (passedGroups.length > 0) {
    tabItems.push(["passed", "Passed"]);
    tabCounts.passed = passedGroups.length;
  }
  for (const did of usedDomains) {
    const d = DOMAIN_FILTERS.find((x) => x.id === did);
    tabItems.push([did, d?.short ?? did]);
    tabCounts[did] = all.filter((p) => domainOf(p) === did).length;
  }
  const activeTab = stateFilter ?? domainSel;

  // A practice of your own, "spin in circles each day", no expert behind
  // it (29 Jul 2026). Both actions live at the right end of the filter bar
  // row (moved the same day, third home: a toolbar row was air, and the
  // summary row it then shared duplicated the eyebrow's count). Mark all
  // done only appears over a visible active list; it acts on that list.
  const addYourOwn = (
    <button className="btn quiet" onClick={() => state.openSheet({ kind: "create-practice" })}>
      + Your own practice
    </button>
  );
  const actions = (
    <>
      {addYourOwn}
      {!stateFilter && list.length > 0 && (
        <button
          className="btn"
          onClick={() =>
            void state.markAll(
              list.map((p) => p.id),
              s.today,
              doneN !== list.length,
            )
          }
        >
          {doneN === list.length ? "Unmark all" : "Mark all done"}
        </button>
      )}
    </>
  );

  // The count is set apart from the label, so two chips never read as one
  // long filter name (used by the second-level topic chips).
  const chip = (on: boolean, onClick: () => void, label: string, n: number) => (
    <button key={label} className={`chip ${on ? "on" : ""}`} onClick={onClick}>
      {label}
      <span className="cn">{n}</span>
    </button>
  );

  // Second level: topics under the chosen domain, only when it splits.
  const subTopics =
    stateFilter || domainSel === "all"
      ? []
      : [
          ...new Set(
            all
              .filter((p) => domainOf(p) === domainSel)
              .map((p) => s.cards[p.cardId]?.topicId)
              .filter((t): t is string => Boolean(t)),
          ),
        ];
  const subChips =
    subTopics.length > 1 ? (
      <div className="chiprow subchips">
        {chip(
          !state.planTopic,
          () => state.setPlanTopic(null),
          "Everything",
          all.filter((p) => domainOf(p) === domainSel).length,
        )}
        {subTopics.map((tid) => {
          const t = s.topics.find((x) => x.id === tid);
          const n = all.filter((p) => s.cards[p.cardId]?.topicId === tid).length;
          return chip(state.planTopic === tid, () => state.setPlanTopic(tid), t?.short ?? tid, n);
        })}
      </div>
    ) : null;

  let body: React.ReactNode;
  if (stateFilter === "later") {
    body = (
      <>
        {paused.map((p) => (
          <PausedRow key={p.id} cardId={p.cardId} planItemId={p.id} until={p.pausedUntil} />
        ))}
        {later.map((x) => (
          <LaterRow key={x.snoozeId} {...x} />
        ))}
      </>
    );
  } else if (stateFilter === "passed") {
    body = (
      <>
        {passedGroups.map((g) => (
          <PassedRow key={g.declineId} group={g} />
        ))}
      </>
    );
  } else {
    body = (
      <>
        {subChips}
        {list.length ? (
          <PlanGrid items={list} />
        ) : (
          <p className="empty">
            {domainSel === "all" ? "Nothing active." : "Nothing active under that filter."}{" "}
            <button className="txt" style={{ textDecoration: "underline" }} onClick={() => state.nav("discover")}>
              See what's new ›
            </button>
          </p>
        )}
      </>
    );
  }

  return {
    nav: "practices",
    title: "My Practice",
    tabs: {
      items: tabItems,
      counts: tabCounts,
      active: activeTab,
      onPick: (t) => state.setPlanDomain(t),
      aside: actions,
    },
    main: body,
  };
}
